<template>
  <div>
    <div
      v-if="show && step === 'lookup'"
      class="modal-mask"
      @click="handleBackdropClick"
    >
      <div class="modal-dialog modal-dialog-centered" @click.stop>
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ lang.get("admin.cdr.lookupTitle") }}
            </h5>
            <button type="button" class="close" @click="close">
              <span>&times;</span>
            </button>
          </div>

          <div class="modal-body">
            <div class="form-group">
              <label>
                {{ lang.get("admin.cdr.lookupType") }}
                <span class="text-danger">*</span>
              </label>
              <select v-model="lookupType" class="form-control">
                <option :value="CDR_SCOPE_TYPE.EXAMINATION">
                  {{ lang.get("admin.cdr.lookupTypeExamination") }}
                </option>
                <option :value="CDR_SCOPE_TYPE.PATIENT">
                  {{ lang.get("admin.cdr.lookupTypePatient") }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label>
                {{
                  lookupType === CDR_SCOPE_TYPE.EXAMINATION
                    ? lang.get("admin.cdr.lookupCaseNo")
                    : lang.get("admin.cdr.lookupSsn")
                }}
                <span class="text-danger">*</span>
              </label>
              <input
                v-model="lookupValue"
                type="text"
                class="form-control"
                @keydown.enter.prevent="search"
              />
            </div>

            <div v-if="lookupError" class="alert alert-danger">
              {{ lookupError }}
            </div>

            <div v-if="lookupResult" class="alert alert-success">
              {{ lookupResultText }}
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="close">
              {{ lang.get("general.modalCancelButton") }}
            </button>
            <button
              v-if="!lookupResult"
              type="button"
              class="btn btn-primary"
              :disabled="!lookupValue.trim() || searching"
              @click="search"
            >
              <span v-if="searching" class="fas fa-spinner fa-spin"></span>
              {{ lang.get("admin.cdr.lookupSearch") }}
            </button>
            <button
              v-if="lookupResult"
              type="button"
              class="btn btn-primary"
              @click="continueToFlag"
            >
              {{ lang.get("admin.cdr.lookupContinue") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <CdrFlagDialog
      :show="show && step === 'flag'"
      :target-type="lookupType"
      :target-id="lookupResult?.id ?? 0"
      :wound-acknowledgement="lookupResult?.wound_acknowledgement"
      :submit-url="flagSubmitUrl"
      @close="close"
      @created="handleFlagCreated"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from "vue";
import { practitionerHttpService } from "../../app/practitioner-portal/store/practitioner-http-service";
import { lang } from "../../i18n";
import CdrFlagDialog from "./CdrFlagDialog.vue";
import { CDR_SCOPE_TYPE, type CdrFlaggableScopeType } from "./cdr-constants";
import { normaliseSwedishSsn } from "./swedish-ssn";
import type { WoundAcknowledgement } from "./wound-acknowledgement";

interface ExaminationLookupResult {
  id: number;
  case_no: string;
  product_name: string | null;
  wound_acknowledgement?: WoundAcknowledgement;
}

interface PatientLookupResult {
  id: number;
  firstname: string;
  lastname: string;
  ssn: string;
}

type LookupResult = ExaminationLookupResult | PatientLookupResult;

const props = defineProps<{
  show: boolean;
  apiBase: string;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "created"): void;
}>();

const step = ref<"lookup" | "flag">("lookup");
const lookupType = ref<CdrFlaggableScopeType>(CDR_SCOPE_TYPE.EXAMINATION);
const lookupValue = ref("");
const lookupResult = ref<LookupResult | null>(null);
const lookupError = ref<string | null>(null);
const searching = ref(false);

const lookupResultText = computed((): string => {
  if (!lookupResult.value) return "";
  if (lookupType.value === CDR_SCOPE_TYPE.EXAMINATION) {
    const r = lookupResult.value as ExaminationLookupResult;
    return lang.get("admin.cdr.lookupExaminationFound", {
      caseNo: r.case_no,
      productName: r.product_name ?? "",
    });
  }
  const r = lookupResult.value as PatientLookupResult;
  return lang.get("admin.cdr.lookupPatientFound", {
    firstname: r.firstname,
    lastname: r.lastname,
    ssn: r.ssn,
  });
});

const flagSubmitUrl = computed((): string => {
  if (!lookupResult.value) return "";
  return lookupType.value === CDR_SCOPE_TYPE.EXAMINATION
    ? props.apiBase + "/examinations/" + lookupResult.value.id + "/flag"
    : props.apiBase + "/patients/" + lookupResult.value.id + "/flag";
});

watch(
  () => props.show,
  (isVisible) => {
    if (isVisible) {
      reset();
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  },
  { immediate: true },
);

// Belt-and-braces cleanup: parents that mount this dialog via `v-if` can tear
// it down before the watcher above sees `show` flip to false (e.g. on the
// close-after-submit flow), leaving body { overflow: hidden } stuck.
onUnmounted(() => {
  if (document.body.style.overflow === "hidden") {
    document.body.style.overflow = "";
  }
});

watch(lookupType, () => {
  lookupValue.value = "";
  lookupResult.value = null;
  lookupError.value = null;
});

function reset() {
  step.value = "lookup";
  lookupType.value = CDR_SCOPE_TYPE.EXAMINATION;
  lookupValue.value = "";
  lookupResult.value = null;
  lookupError.value = null;
  searching.value = false;
}

function close() {
  emit("close");
}

function handleBackdropClick(event: Event) {
  if (event.target === event.currentTarget) close();
}

async function search() {
  if (!lookupValue.value.trim()) return;

  searching.value = true;
  lookupError.value = null;
  lookupResult.value = null;

  const url =
    lookupType.value === CDR_SCOPE_TYPE.EXAMINATION
      ? props.apiBase + "/lookup-examination"
      : props.apiBase + "/lookup-patient";

  let payload: { case_no: string } | { ssn: string };
  if (lookupType.value === CDR_SCOPE_TYPE.EXAMINATION) {
    payload = { case_no: lookupValue.value.trim() };
  } else {
    // Patient SSN search at admin level only normalises Swedish personnummer; other
    // identifiers (NHS numbers, reservation numbers, etc.) flow through unchanged.
    const trimmed = lookupValue.value.trim();
    payload = { ssn: normaliseSwedishSsn(trimmed) ?? trimmed };
  }

  try {
    const response = await practitionerHttpService.post(url, payload);
    lookupResult.value = response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      lookupError.value = lang.get("admin.cdr.lookupNotFound");
    } else {
      lookupError.value =
        lang.get("admin.cdr.submitError") ??
        "An error occurred. Please try again.";
    }
  } finally {
    searching.value = false;
  }
}

function continueToFlag() {
  step.value = "flag";
}

function handleFlagCreated() {
  emit("created");
  close();
}
</script>

<style scoped>
.modal-mask {
  text-align: left;
  white-space: normal;
}
</style>
